import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useHasRole, useRetryStory, useStoryChapter, useStoryChapters, useStorySummary, useThreads } from '../api/hooks';
import type { PersonalChapterRow, ProseLine, StoryChapterDetail } from '../api/types';
import { PageHeader } from '../app/Shell';
import { CHAPTER_STATUS, THREAD_STATUS } from '../app/labels';
import { Badge, Button, Card, ConfirmDialog, DataTable, Empty, ErrorNote, Icon, Id, Plate, QueryView, ReasonField, Segmented, When, reasonError, toast } from '../ui';
import { LoadMore } from './parts';
import s from './Story.module.css';

type Filter = 'all' | 'failed' | 'issues' | 'ready' | 'generating';

/** The AI writer's output: a list of chapters on the left, the selected one read as a script on the right. */
export function StoryPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'threads' ? 'threads' : 'chapters';
  return (
    <>
      <PageHeader
        title="故事 QA"
        description="每位玩家的 AI 章節：失敗或未過質量檢查的、最近寫成的，以及留下的未完的線。"
        actions={
          <Segmented
            label="分頁"
            value={tab}
            options={[
              { value: 'chapters', label: 'AI 章節' },
              { value: 'threads', label: '未完的線' },
            ]}
            onChange={(v) => setParams(v === 'threads' ? { tab: v } : {})}
          />
        }
      />
      {tab === 'chapters' ? <Chapters /> : <Threads />}
    </>
  );
}

function Chapters() {
  const [params, setParams] = useSearchParams();
  const filter: Filter = params.get('status') === 'failed' ? 'failed' : params.get('issues') ? 'issues' : params.get('status') === 'ready' ? 'ready' : params.get('status') === 'generating' ? 'generating' : 'all';
  const userId = params.get('user') ?? '';
  const [npcId, setNpcId] = useState('');
  const summary = useStorySummary();
  const list = useStoryChapters({
    status: filter === 'failed' || filter === 'ready' || filter === 'generating' ? filter : undefined,
    issues: filter === 'issues' || undefined,
    userId: userId || undefined,
    npcId: npcId.trim() || undefined,
  });
  const selected = params.get('chapter') ?? list.rows[0]?._id ?? null;

  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true });
  };
  const pick = (f: Filter) => set({ status: f === 'failed' || f === 'ready' || f === 'generating' ? f : null, issues: f === 'issues' ? '1' : null, chapter: null });

  const chips: Array<{ value: Filter; label: string; n?: number | undefined; tone?: 'danger' | 'warn' }> = [
    { value: 'all', label: '全部' },
    { value: 'failed', label: '失敗', n: summary.data?.failed, tone: 'danger' },
    { value: 'issues', label: '有質量問題', n: summary.data?.issues, tone: 'warn' },
    { value: 'ready', label: '待玩', n: summary.data?.ready },
    { value: 'generating', label: '撰寫中', n: summary.data?.generating },
  ];

  return (
    <div className={s.split}>
      <section className={s.listPane}>
        <div className={s.chips} role="group" aria-label="篩選">
          {chips.map((c) => (
            <button key={c.value} type="button" className={s.chip} aria-pressed={filter === c.value} onClick={() => pick(c.value)}>
              {c.label}
              {c.n ? <span className={s.chipN} data-tone={c.tone}>{c.n}</span> : null}
            </button>
          ))}
        </div>
        <div className={s.listFilters}>
          <input type="text" value={npcId} onChange={(e) => setNpcId(e.target.value)} placeholder="角色 ID，例如 mia" aria-label="角色" />
          {userId ? (
            <button type="button" className={s.userChip} onClick={() => set({ user: null, chapter: null })} title="取消玩家篩選">
              玩家 {userId.slice(0, 8)} ✕
            </button>
          ) : null}
        </div>
        <QueryView query={list} loading="載入章節…">
          {() =>
            list.rows.length ? (
              <div className={s.list}>
                {list.rows.map((r) => (
                  <ChapterCard key={r._id} r={r} selected={r._id === selected} onClick={() => set({ chapter: r._id })} />
                ))}
                <LoadMore q={list} />
              </div>
            ) : (
              <Empty hint="換一個篩選，或清除角色 / 玩家條件。">沒有符合的章節。</Empty>
            )
          }
        </QueryView>
      </section>
      <section className={s.readerPane}>{selected ? <Reader id={selected} /> : <Empty hint="在左邊揀一章來閱讀。">未選擇章節。</Empty>}</section>
    </div>
  );
}

function ChapterCard({ r, selected, onClick }: { r: PersonalChapterRow; selected: boolean; onClick: () => void }) {
  const st = r.status === 'failed' ? { label: '失敗', tone: 'danger' as const } : r.reviewIssues?.length ? { label: '有問題', tone: 'warn' as const } : CHAPTER_STATUS[r.status];
  return (
    <button type="button" className={s.card} aria-current={selected || undefined} onClick={onClick}>
      <div className={s.cardTop}>
        <strong className={s.cardTitle}>{r.title ?? <span className="faint">（未寫成）</span>}</strong>
        <Badge tone={st.tone}>{st.label}</Badge>
      </div>
      <div className={s.cardMeta}>
        <span>
          {r.lane} · 第 {r.seq} 章
        </span>
        <When at={r.updatedAt} />
      </div>
      {r.emotionalBeat && (
        <div className={s.cardBeat}>
          {r.emotionalBeat}
          {r.heartSkip ? ` · ${r.heartSkip}` : ''}
        </div>
      )}
    </button>
  );
}

function Reader({ id }: { id: string }) {
  const q = useStoryChapter(id);
  return <QueryView query={q} loading="載入內文…">{(c) => <ReaderBody key={c._id} c={c} />}</QueryView>;
}

function ReaderBody({ c }: { c: StoryChapterDetail }) {
  const canRetry = useHasRole('support');
  const [retrying, setRetrying] = useState(false);
  const [reason, setReason] = useState('');
  const retry = useRetryStory();
  const ref = (n: number) => document.getElementById(`beat-${n}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className={s.reader}>
      <div className="spread" style={{ alignItems: 'flex-start' }}>
        <h2 className={s.readerTitle}>
          <Plate large>{c.prose?.title ?? c.title ?? '（未寫成）'}</Plate>
        </h2>
        <Badge tone={CHAPTER_STATUS[c.status].tone}>{CHAPTER_STATUS[c.status].label}</Badge>
      </div>
      <div className={s.metaChips}>
        <span>
          <Icon name="heart" size={15} /> 情緒節拍 <b>{c.emotionalBeat ?? '—'}</b>
        </span>
        <span>
          <Icon name="flower" size={15} /> 心跳 <b>{c.heartSkip ?? '（友情章節）'}</b>
        </span>
        <span>
          <Icon name="pin" size={15} /> 地點 <b>{(c.spots ?? []).join('、') || c.hook.spot || c.hook.location}</b>
        </span>
      </div>
      <div className={s.facts}>
        <span>
          玩家 <Link to={`/users/${c.userId}`}>{c.userId.slice(0, 8)}</Link>
        </span>
        <span>
          {c.lane} · 第 {c.seq} 章 · {c.locale ?? '—'}
        </span>
        <span>
          引子 {c.hook.npcId ?? '—'}：{c.hook.reason}
        </span>
        <span>
          更新 <When at={c.updatedAt} />
        </span>
      </div>

      {c.status === 'failed' && (
        <div className="callout" data-tone="danger">
          <span className="callIcon">✕</span>
          <div className="callBody">
            <strong>撰寫失敗</strong>
            <pre className={s.error}>{c.error ?? '沒有錯誤訊息。'}</pre>
          </div>
          <Button variant="primary" disabled={!canRetry} title={canRetry ? undefined : '需要客服角色'} onClick={() => setRetrying(true)}>
            重新撰寫
          </Button>
        </div>
      )}
      {!!c.reviewIssues?.length && (
        <div className="callout" data-tone="warn">
          <span className="callIcon">⚠</span>
          <div className="callBody">
            <strong>質量檢查問題（改寫一次後仍在）</strong>
            <ul className={s.issues}>
              {c.reviewIssues.map((i) => (
                <li key={i}>{i}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {(c.endHook || c.opensThread) && (
        <div className={s.hooks}>
          {c.endHook && (
            <div>
              <span className="faint small">結尾鉤子</span>
              <p>{c.endHook}</p>
            </div>
          )}
          {c.opensThread && (
            <div>
              <span className="faint small">留下的線 · {c.opensThread.kind}</span>
              <p>{c.opensThread.text}</p>
            </div>
          )}
          {c.paysOffThread && (
            <div>
              <span className="faint small">收回的線</span>
              <p>
                <Id value={c.paysOffThread} />
              </p>
            </div>
          )}
        </div>
      )}

      {c.prose ? (
        <>
          {c.prose.beats.length > 2 && (
            <nav className={s.toc} aria-label="跳到">
              {c.prose.beats.map((b, i) => (
                <button key={b.id} type="button" onClick={() => ref(i)}>
                  第 {i + 1} 幕
                </button>
              ))}
            </nav>
          )}
          <Prose c={c} />
        </>
      ) : (
        <Empty hint={c.status === 'failed' ? '重新撰寫後就會有內文。' : '章節寫成後就會在這裏出現。'}>這一章還沒有內文。</Empty>
      )}

      <ConfirmDialog
        open={retrying}
        title="重新撰寫這一章？"
        confirmLabel="重新撰寫"
        busy={retry.isPending}
        canConfirm={!reasonError(reason)}
        onCancel={() => setRetrying(false)}
        onConfirm={() =>
          retry.mutate(
            { chapterId: c._id, reason: reason.trim() },
            {
              onSuccess: () => {
                setRetrying(false);
                toast('已排隊重新撰寫，完成後狀態會變為「待玩」');
              },
            },
          )
        }
        error={retry.isError ? <ErrorNote error={retry.error} /> : null}
      >
        <div className="callout" data-tone="ok">
          <span className="callBody">用同一個引子經 story.generate 再寫一次（會產生 AI 花費）。完成後玩家會在 App 收到這一章。同一條線已有其他未完成章節時不能重試。</span>
        </div>
        <ReasonField value={reason} onChange={setReason} autoFocus />
      </ConfirmDialog>
    </div>
  );
}

function Line({ l }: { l: ProseLine }) {
  return l.speaker ? (
    <p>
      <span className="speaker">{l.speaker === 'player' ? '玩家' : l.speaker}</span>
      {l.text}
    </p>
  ) : (
    <p className="narration">{l.text}</p>
  );
}

function Prose({ c }: { c: StoryChapterDetail }) {
  const p = c.prose!;
  return (
    <article className="prose">
      <p className="narration">{p.synopsis}</p>
      {p.beats.map((b, i) => (
        <section key={b.id} className="stack" style={{ gap: 8 }} id={`beat-${i}`}>
          <div className="beatHead">
            第 {i + 1} 幕 · {b.background}
          </div>
          {b.lines.map((l, j) => (
            <Line key={j} l={l} />
          ))}
          {b.choices.length > 0 && (
            <div className="stack" style={{ gap: 6 }}>
              {b.choices.map((ch, k) => (
                <div key={ch.id} className="choice stack" style={{ gap: 4 }}>
                  <div className="choiceLabel">
                    <span className="choiceNo">{k + 1}</span>
                    {ch.label}
                    {ch.check && <span className="faint small"> · 判定 {ch.check}</span>}
                  </div>
                  {ch.normal.map((l, j) => (
                    <Line key={j} l={l} />
                  ))}
                </div>
              ))}
            </div>
          )}
        </section>
      ))}
      {p.outcomes.map((o) => (
        <section key={o.id} className="stack" style={{ gap: 8 }}>
          <div className="beatHead">結局 · {o.title}</div>
          <p className="narration">{o.summary}</p>
          {o.closing.map((l, j) => (
            <Line key={j} l={l} />
          ))}
        </section>
      ))}
    </article>
  );
}

function Threads() {
  const [status, setStatus] = useState<'' | 'open' | 'paid' | 'expired'>('open');
  const [userId, setUserId] = useState('');
  const threads = useThreads({ status: status || undefined, userId: userId.trim() || undefined });
  return (
    <Card flush>
      <div className="filters">
        <label>
          狀態
          <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="">全部</option>
            {(['open', 'paid', 'expired'] as const).map((st) => (
              <option key={st} value={st}>
                {THREAD_STATUS[st].label}
              </option>
            ))}
          </select>
        </label>
        <label>
          玩家 ID
          <input type="text" value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="user ID" />
        </label>
      </div>
      <QueryView query={threads} loading="載入未完的線…">
        {(rows) => (
          <DataTable
            rows={rows}
            rowKey={(r) => r._id}
            pageSize={20}
            empty="沒有符合的線。"
            emptyHint="章節留下承諾、誤會或未拆的信時，就會在這裏出現。"
            columns={[
              { key: 'at', header: '更新', render: (r) => <When at={r.updatedAt} />, sort: (r) => r.updatedAt },
              { key: 'kind', header: '類型', render: (r) => <code>{r.kind}</code>, sort: (r) => r.kind },
              { key: 'text', header: '內容', render: (r) => <span style={{ display: 'block', maxWidth: 520 }}>{r.text}</span> },
              { key: 'npcs', header: '角色', render: (r) => r.npcIds.join('、') },
              { key: 'payoff', header: '收線時機', render: (r) => r.payoffWhen ?? <span className="faint">—</span> },
              { key: 'status', header: '狀態', render: (r) => <Badge tone={THREAD_STATUS[r.status].tone}>{THREAD_STATUS[r.status].label}</Badge> },
              { key: 'user', header: '玩家', render: (r) => (r.userId ? <Link to={`/users/${r.userId}`}>{r.userId.slice(0, 8)}</Link> : '—') },
            ]}
          />
        )}
      </QueryView>
    </Card>
  );
}
